import {
  LitElement,
  html,
  css,
  classMap,
  t,
  paths,
  unsafeHTML,
} from '@hh/global.js'

class HHMenuFeaturedPromos extends LitElement {
  static properties = {
    paused: {
      type: Boolean,
      reflect: true,
    },
    _promoHeight: {
      type: Number,
    },
  }

  constructor() {
    super()
    this.paused = true
    this._promoHeight = 0

    this.isMobile = matchMedia('(max-width: 989px)').matches
    if (this.isMobile) {
      this.paused = false
    }
  }

  connectedCallback() {
    super.connectedCallback()
    this.lastPromo.addEventListener(
      'animationend',
      this.onLastPromoAnimationEnd.bind(this)
    )

    window.addEventListener('mega-menu-open', this.onMenuOpen.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('mega-menu-open', this.onMenuOpen)
  }

  onMenuOpen() {
    this.paused = false
  }

  onLastPromoAnimationEnd() {
    this.stopAnimation()
    setTimeout(() => {
      this.startAnimation()
    }, 0)
  }

  stopAnimation() {
    this.allPromos.forEach((promo) => (promo.style.animation = 'none'))
    this.allDots.forEach((dot) => dot.classList.add('reset-animation'))
  }

  startAnimation() {
    this.allPromos.forEach((promo) => (promo.style.animation = ''))
    this.allDots.forEach((dot) => dot.classList.remove('reset-animation'))
  }

  onToggleClick() {
    this.paused = !this.paused
  }

  onFocusIn() {
    this.stopAnimation()
  }

  onFocusOut() {
    this.startAnimation()
  }

  get lastPromo() {
    const promos = this.querySelectorAll('[slot="promo"]')
    return promos[promos.length - 1]
  }

  get allPromos() {
    return this.querySelectorAll('[slot="promo"]')
  }

  get allDots() {
    return this.querySelectorAll('[slot="dot"]')
  }

  render() {
    return html`
      <div class="container">
        <div class="wrapper">
          <div class="dots">
            <slot
              name="dot"
              class=${classMap({
                'pause-animation': this.paused,
              })}
            ></slot>
          </div>
          <div class="messages">
            <slot
              name="promo"
              class=${classMap({
                'pause-animation': this.paused,
              })}
              @focusin=${this.onFocusIn}
              @focusout=${this.onFocusOut}
            ></slot>
          </div>
        </div>
        <button
          class="toggle"
          @click=${this.onToggleClick}
          aria-label=${this.paused
            ? t('accessibility.play')
            : t('accessibility.pause')}
        >
          ${this.paused ? unsafeHTML(paths.play) : unsafeHTML(paths.pause)}
        </button>
      </div>
    `
  }

  static styles = css`
    :host {
      box-sizing: border-box;
      display: block;
      background-color: var(--hh-color-background);
      margin: 1.7500rem 0 2.2500rem;
    }

    @media (min-width: 990px) {
      :host {
        margin: 0;
      }
    }

    .container {
      height: 100%;
      position: relative;
    }

    .wrapper {
      position: relative;
      height: 100%;
      width: 100%;
    }

    @media (min-width: 990px) {
      .wrapper {
        width: 75%;
      }
    }

    .messages {
      display: grid;
      grid-template-columns: 1fr;
      align-items: center;
    }

    ::slotted([slot='promo']) {
      opacity: 0;
      grid-row-start: 1;
      grid-column-start: 1;
      width: 85%;
    }

    @media (min-width: 990px) {
      ::slotted([slot='promo']) {
        width: 100%;
      }
    }

    ::slotted([slot='promo']:focus) {
      opacity: 1;
      animation: none;
      transform: translate3d(0, 0, 0);
    }

    .dots {
      display: flex;
      column-gap: 1.0000rem;
      margin-bottom: 1.3750rem;
    }

    ::slotted([slot='dot']) {
      display: block;
      height: 0.0625rem;
      width: 100%;
      background-color: var(--hh-color-night-x-light-opacity);
      overflow: hidden;
    }

    ::slotted([slot='dot'])::after {
      content: '';
      display: block;
      height: 0.0625rem;
      transform: translateX(-100%);
      background-color: var(--hh-color-night);
    }

    .toggle {
      position: absolute;
      bottom: 0;
      right: 0;
      width: 2.6250rem;
      height: 2.6250rem;
      border-radius: 50%;
      border: none;
      color: var(--hh-color-foreground);
      background-color: var(--hh-color-light);
      cursor: pointer;
      padding: 0;
      fill: currentColor;
    }

    .toggle svg {
      fill: transparent;
      transition: fill var(--hh-duration-default) ease-in-out;
    }

    @media (hover: hover) {
      .toggle:is(:focus-visible, :hover) svg {
        fill: currentColor;
      }
    }
  `
}

customElements.define('hh-menu-featured-promos', HHMenuFeaturedPromos)
